import moment from 'moment-timezone';
import helpers from '../economy/helpers/index.js';
import { VoucherCategory } from '../economy/vouchers/index.js';
import { CountryCode, addressToString } from '../index.js';
import { InteractionEventType } from '../interactions/events/index.js';
import { InteractionType } from '../interactions/index.js';
import { TemplateTag } from '../templates/index.js';
import { UpdateType } from '../updates/index.js';
import { sortBy } from '../util/index.js';
import { DKcourts } from './countries/denmark.js';
import { GLcourts } from './countries/greenland.js';
export var UdbetalingsRekvisitioner;
(function (UdbetalingsRekvisitioner) {
    UdbetalingsRekvisitioner["Politifremstilling"] = "Politifremstilling";
    UdbetalingsRekvisitioner["Betalingsp\u00E5krav"] = "Betalingsp\u00E5krav";
    UdbetalingsRekvisitioner["Fogedrekvisition"] = "Fogedrekvisition";
})(UdbetalingsRekvisitioner || (UdbetalingsRekvisitioner = {}));
export const courts = [...DKcourts, ...GLcourts];
export const courtByCountryCode = {
    [CountryCode.DK]: DKcourts,
    [CountryCode.GL]: [...GLcourts, ...DKcourts],
};
export const getCourtByCountryCode = (countryCode) => {
    // @ts-ignore
    if (countryCode && courtByCountryCode[countryCode]) {
        // @ts-ignore
        return courtByCountryCode[countryCode];
    }
    return null;
};
export const getDirector = (debtors) => {
    const virksomhedsformer = [
        'Anpartsselskab',
        'Aktieselskab',
        'Iværksætterselskab',
    ];
    const relationReasons = ['DIREKTØR', 'ADM. DIR.', 'DIREKTION'];
    const ff = debtors.reduce((acc, debtor) => {
        if (debtor.details.virksomhedsform &&
            virksomhedsformer.includes(debtor.details.virksomhedsform)) {
            return acc.concat((debtor.details.companyRelations || []).reduce((abb, relation) => {
                if (relation.type === 'PERSON' &&
                    relation.name &&
                    relation.relationReasons.some((rr) => relationReasons.includes(rr.value))) {
                    return abb.concat({
                        name: `${relation.name} (${relation.relationReasons
                            .filter((rr) => relationReasons.includes(rr.value))
                            .map((rr) => rr.value)
                            .join(', ')})`,
                        address: relation.address?.address &&
                            relation.address?.address !== 'null'
                            ? relation.address
                            : null,
                    });
                }
                return abb;
            }, []));
        }
        return acc;
    }, []);
    return ff && !!ff.length ? ff[0] : null;
};
export const getDirectorNameAddress = (debtors) => {
    const director = getDirector(debtors);
    if (!director) {
        return undefined;
    }
    return director.address !== null
        ? `${director.name}, ${addressToString(director.address)}`
        : director.name;
};
export const getFuldtAnsvaligeDeltagere = (debtors) => {
    const virksomhedsformer = [
        'Enkeltmandsvirksomhed',
        'Interessentskab',
        'Personligt ejet Mindre Virksomhed',
    ];
    const ff = debtors.reduce((acc, debtor) => {
        if (debtor.details.virksomhedsform &&
            virksomhedsformer.includes(debtor.details.virksomhedsform)) {
            return acc.concat((debtor.details.companyRelations || []).reduce((abb, relation) => {
                if (relation.type === 'PERSON' && relation.name) {
                    return abb.concat({
                        name: relation.name,
                        address: relation.address,
                    });
                }
                return abb;
            }, []));
        }
        return acc;
    }, []);
    return ff && !!ff.length ? ff : null;
};
export const getInterestDates = (timezone, caseVouchers, interestDates) => interestDates ||
    (caseVouchers &&
        helpers.vouchers
            .postProcessing(caseVouchers, {
            includeIrrecoverableVouchers: false,
            includeDeletedVouchers: false,
            includeExpiredVouchers: false,
        })
            .filter((c) => c.category === 'interest')
            .filter((c) => c.interestStartDate || c.dueDate)
            .filter((c) => c.interestStartDate || c.dueDate)
            .map((c) => c.interestStartDate || c.dueDate)
            .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
            .map((d) => moment(d).locale('da').tz(timezone).format('LL'))
            .join(', ')) ||
    '';
export const getCaseInterest = (caseVouchers, customInterest) => {
    if (customInterest) {
        return customInterest;
    }
    const interestOnCase = caseVouchers.find((v) => v.category === VoucherCategory.INTEREST)
        ?.interest || '';
    const caseInterest = typeof interestOnCase == 'number'
        ? (interestOnCase * 100).toFixed(2).toString().replace('.', ',')
        : interestOnCase;
    return caseInterest;
};
export const getWarnings = (interactions, templates) => {
    const warningTemplates = templates.reduce((acc, template) => {
        if (template.tags.map(({ tag }) => tag).includes(TemplateTag.BPKWarning)) {
            acc[template.templateId] = true;
        }
        return acc;
    }, {});
    const warnings = interactions.filter((interaction) => {
        const isWarning = interaction.templateId && warningTemplates[interaction.templateId];
        if (isWarning) {
            return interaction;
        }
    });
    return sortBy(warnings, (w) => w.createdAt);
};
const isWarningDelivered = (interaction) => {
    if (interaction.type === InteractionType.Email) {
        return interaction.events.some((e) => e.type === InteractionEventType.Delivered);
    }
    else if (interaction.type === InteractionType.Letter) {
        return (interaction.events.some((e) => e.type === InteractionEventType.TransferredToPrintAndDistribution) &&
            !interaction.events.some((e) => e.type === InteractionEventType.Failed));
    }
    else if (interaction.type === InteractionType.Eboks) {
        return interaction.events.some((e) => e.type === InteractionEventType.Delivered);
    }
    return false;
};
export const getWarningDate = (interactions, updates, templates) => {
    const warnings = getWarnings(interactions, templates);
    const warning = warnings.find((w) => isWarningDelivered(w)) ?? warnings[0];
    const warningUpdate = getWarningUpdates(updates)[0];
    const warningDate = warning ? warning.createdAt : null;
    if (warningDate && warningUpdate) {
        if (moment(warningDate).isBefore(moment(warningUpdate.pausedAt))) {
            return moment(warningDate).format();
        }
        return moment(warningUpdate.pausedAt).format();
    }
    else if (warningUpdate) {
        return moment(warningUpdate.pausedAt).format();
    }
    else {
        return warningDate;
    }
};
export const getWarningUpdates = (updates) => {
    const warningUpdates = updates.filter((update) => {
        if (update.updateTypeId === UpdateType.BPKWARNED) {
            return true;
        }
        return false;
    });
    return sortBy(warningUpdates, (w) => w.pausedAt);
};
export const getCourt = (contactPerson, court) => {
    if (court || court === null) {
        return court;
    }
    const contactPersonCourt = contactPerson &&
        contactPerson.details.courtId &&
        courts.find((c) => contactPerson.details.courtId &&
            [c.id, c.courtId].includes(contactPerson.details.courtId));
    if (contactPersonCourt) {
        return contactPersonCourt;
    }
};
//# sourceMappingURL=index.js.map